import React from 'react';
import { Autocomplete, Box, TextField } from '@mui/material';
import TagChip, { TagDot } from './TagChip';

// Offered even before any channel uses them.
const SUGGESTED_TAGS = ['Mitom', 'Sam', 'Chi Vy', 'Sister & Cousin'];
const MAX_TAGS = 20;
const MAX_TAG_LENGTH = 40;

const tagKey = (tag) => tag.toLowerCase();

function cleanTag(text) {
  return text.trim().replace(/\s+/g, ' ');
}

// The user's existing tags plus the suggestions, once each. An existing tag's
// spelling wins over a suggestion's.
export function tagChoices(existingTags) {
  const byKey = new Map();
  for (const tag of [...existingTags, ...SUGGESTED_TAGS]) {
    if (!byKey.has(tagKey(tag))) byKey.set(tagKey(tag), tag);
  }
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * Picks a channel's tags from the user's existing ones, or adds new ones by
 * typing a name and pressing Enter.
 */
export default function ChannelTagsField({ value, onChange, existingTags = [] }) {
  const choices = tagChoices(existingTags);

  // Typed tags reuse the spelling of a matching choice, so "chi vy" joins
  // "Chi Vy" instead of starting a second tag.
  function change(_, next) {
    const tags = new Map();
    for (const item of next) {
      const tag = cleanTag(item);
      if (!tag || tags.has(tagKey(tag))) continue;
      tags.set(tagKey(tag), choices.find((choice) => tagKey(choice) === tagKey(tag)) || tag);
    }
    onChange([...tags.values()].slice(0, MAX_TAGS));
  }

  return (
    <Autocomplete
      multiple
      freeSolo
      autoSelect
      filterSelectedOptions
      options={choices}
      value={value}
      onChange={change}
      renderValue={(tags, getItemProps) =>
        tags.map((tag, index) => {
          const { key, ...itemProps } = getItemProps({ index });
          return <TagChip key={key} tag={tag} {...itemProps} />;
        })
      }
      renderOption={({ key, ...optionProps }, tag) => (
        <Box component="li" key={key} {...optionProps} sx={{ gap: 1.25 }}>
          <TagDot tag={tag} />
          {tag}
        </Box>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Tags"
          placeholder={value.length ? '' : 'Choose or type a tag'}
          helperText="Choose a tag, or type a new one and press Enter."
          inputProps={{ ...params.inputProps, maxLength: MAX_TAG_LENGTH }}
        />
      )}
    />
  );
}
